import { Button } from "reactstrap";

import {
    ConfigField,
    FieldDef,
    IConfig,
} from "Pages/Setup/Connect/connectShared";
import { BILLY_FIELDS } from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

/** Default the connector should fall back to when billy.api.base_url is blank.
 * Shown here so the field can genuinely be left empty. */
const BILLY_DEFAULT_BASE_URL = "https://app.billyforinsurance.com/api/v1";

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    goTo: (view: string) => void;
}

export default function BillyConnect({ find, onEdit, goTo }: Props) {
    const requiredFields = BILLY_FIELDS.filter((f) => f.required);
    const requiredMissing = requiredFields.filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const connected = requiredMissing === 0;

    // Document downloads use the Billy web session, not the API, so the web
    // credentials only matter when that switch is on.
    const webEnabled = ["true", "1", "yes", "y", "on"].includes(
        (find("billy.web.enabled")?.keyValue ?? "").trim().toLowerCase()
    );
    const webCredsMissing = webEnabled
        ? ["billy.web.email", "billy.web.password", "billy.web.team_id"].filter(
              (s) => !find(s)?.keyValue
          ).length
        : 0;

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoBilly}`}>B</div>
                <div>
                    <div className={styles.pheadName}>Billy</div>
                    <div className={styles.pheadSub}>
                        Insurance compliance · vendor &amp; subcontract COIs
                    </div>
                </div>
                {connected ? (
                    <span className={`${styles.pill} ${styles.pillGood}`}>
                        <span className={styles.dot} /> Connected
                    </span>
                ) : requiredMissing < requiredFields.length ? (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                        <span className={styles.dot} /> Setup in progress
                    </span>
                ) : (
                    <span className={`${styles.pill} ${styles.pillBad}`}>
                        <span className={styles.dot} /> Not connected
                    </span>
                )}
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${styles.tabActive}`}>
                    Connect
                </button>
                <button
                    className={styles.tab}
                    onClick={() => goTo("billytest")}
                >
                    Test Connection
                </button>
            </div>

            <div className={styles.wz}>
                <div className={styles.wzLeft}>
                    <h2>Authorize Dimension in Billy</h2>
                    <p className={styles.lead}>
                        Billy issues API client credentials per team. Certificate
                        and endorsement downloads additionally need a Billy
                        web-session login, because those files are not served by
                        the API.
                    </p>

                    <ol className={styles.steps}>
                        <li>
                            Ask your Billy contact to issue an{" "}
                            <strong>API client ID (a GUID) and secret</strong>{" "}
                            for Dimension.
                        </li>
                        <li>
                            Enter the API credentials and your{" "}
                            <strong>Billy team identifier</strong> below — it is
                            the <span className={styles.mono}>&lt;team&gt;</span>{" "}
                            segment of your Billy URL.
                        </li>
                        <li>
                            To sync COI documents, invite the Calance service
                            address{" "}
                            <span className={styles.mono}>
                                &lt;client&gt;@calance.us
                            </span>{" "}
                            as a Billy web user. Calance owns that login and
                            sets its password.
                        </li>
                    </ol>

                    {BILLY_FIELDS.map((field: FieldDef, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={onEdit}
                        />
                    ))}

                    {!find("billy.api.base_url")?.keyValue && (
                        <p className={styles.mapNote}>
                            Base URL is blank — the connector uses{" "}
                            <span className={styles.mono}>
                                {BILLY_DEFAULT_BASE_URL}
                            </span>
                            .
                        </p>
                    )}
                </div>

                <div className={styles.wzRight}>
                    <p className={styles.rlabel}>Where to do this in Billy</p>
                    <p className={styles.crumb}>
                        <strong>Settings &gt; Requirement Groups</strong> — the
                        source of every compliance type Dimension will send.
                    </p>
                    <div className={styles.shot}>
                        <div className={styles.shotBar}>
                            <i />
                            <i />
                            <i />
                            <span className={styles.shotUrl}>
                                Settings › Requirement Groups
                            </span>
                        </div>
                        <div className={styles.shotBody}>
                            <div className={styles.shotStep}>
                                <span className={styles.shotBadge}>1</span>
                                <div>
                                    <b>Company-level requirement groups</b>
                                    <span className={styles.mono}>
                                        Settings › Requirement Groups › Company
                                        Level
                                    </span>
                                    <span>
                                        Drives the vendor-level compliance types
                                        Intacct must have.
                                    </span>
                                </div>
                            </div>
                            <div className={styles.shotStep}>
                                <span className={styles.shotBadge}>2</span>
                                <div>
                                    <b>Project-level requirement groups</b>
                                    <span className={styles.mono}>
                                        Settings › Requirement Groups › Project
                                        Level
                                    </span>
                                    <span>
                                        Drives the project (subcontract)
                                        compliance types from the Coverages tab
                                        on a contract.
                                    </span>
                                </div>
                            </div>
                            <div className={styles.shotStep}>
                                <span className={styles.shotBadge}>3</span>
                                <div>
                                    <b>Invite the Calance service login</b>
                                    <span className={styles.mono}>
                                        &lt;client&gt;@calance.us
                                    </span>
                                    <span>
                                        A Calance-owned web user rather than a
                                        person's account, so COI downloads
                                        survive staff changes.
                                    </span>
                                </div>
                            </div>
                            <div className={styles.shotStep}>
                                <span className={styles.shotBadge}>4</span>
                                <div>
                                    <b>Grant vendor + subcontract visibility</b>
                                    <span>
                                        The service user must see every vendor
                                        and contract you expect Dimension to
                                        sync.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className={styles.shotCap}>
                        Each Billy requirement is mapped to its Intacct
                        compliance types on <b>2 · Compliance mapping</b>.
                    </p>
                </div>
            </div>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo("intacct")}>
                    Set up Sage Intacct next →
                </Button>
                <span className={styles.saveState}>
                    {connected
                        ? webCredsMissing
                            ? `✓ Billy API credentials set · ${webCredsMissing} web value${
                                  webCredsMissing > 1 ? "s" : ""
                              } missing for document downloads.`
                            : "✓ Billy credentials are set."
                        : `${requiredMissing} required value${
                              requiredMissing > 1 ? "s" : ""
                          } missing.`}
                </span>
            </div>
        </div>
    );
}
